import { useDraggable } from '@dnd-kit/core';
import { Link } from 'react-router-dom';
import type { ContactListItem } from '@cockpit/shared';
import { formatEuro, LABEL_TERRITOIRE_CONTACT, LABEL_TYPE_CLIENT } from '../../../lib/format';

function relatif(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffMs = now - t;
  if (!Number.isFinite(diffMs)) return '—';
  const j = Math.floor(diffMs / (24 * 3600 * 1000));
  if (j < 0) return 'à venir';
  if (j === 0) return "aujourd'hui";
  if (j === 1) return 'hier';
  if (j < 7) return `il y a ${String(j)} j`;
  if (j < 30) return `il y a ${String(Math.floor(j / 7))} sem`;
  if (j < 365) return `il y a ${String(Math.floor(j / 30))} mois`;
  return `il y a ${String(Math.floor(j / 365))} an${j >= 730 ? 's' : ''}`;
}

interface Props {
  contact: ContactListItem;
}

export function KanbanCard({ contact }: Props): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `contact-${String(contact.id)}`,
    data: { contactId: contact.id, currentStatut: contact.statut_pipeline },
  });

  const style: React.CSSProperties = {
    transform:
      transform !== null
        ? `translate3d(${String(transform.x)}px, ${String(transform.y)}px, 0)`
        : undefined,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="bg-slate-900 border border-slate-700 rounded-lg p-3 mb-2 cursor-grab active:cursor-grabbing hover:border-floryon-green/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <Link
          to={`/contacts/${String(contact.id)}`}
          onClick={(e): void => e.stopPropagation()}
          className="text-sm font-medium text-slate-100 hover:text-floryon-green truncate flex-1"
        >
          {contact.societe ?? `${contact.prenom ?? ''} ${contact.nom}`.trim()}
        </Link>
        <span className="text-[10px] text-slate-500 shrink-0">
          {LABEL_TERRITOIRE_CONTACT[contact.territoire]}
        </span>
      </div>
      {contact.societe !== null && (
        <div className="text-xs text-slate-500 truncate mb-2">
          {`${contact.prenom ?? ''} ${contact.nom}`.trim()}
        </div>
      )}
      <div className="flex items-center justify-between text-[11px] text-slate-400">
        <span>{LABEL_TYPE_CLIENT[contact.type_client]}</span>
        {contact.valeur_estimee_centimes > 0 && (
          <span className="text-floryon-green font-semibold">
            {formatEuro(contact.valeur_estimee_centimes)}
          </span>
        )}
      </div>
      <div className="mt-1 text-[10px] text-slate-500">
        Dernière interaction : {relatif(contact.date_derniere_interaction)}
      </div>
    </div>
  );
}
